import { apiClient } from '@/utils/api';

export interface Activity {
  id: string;
  practiceId: string;
  practice?: {
    id: string;
    title: string;
    practiceNumber?: string | null;
    documentPath?: string | null;
    type?: {
      name: string;
      code: string;
    };
    client?: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
  userId?: string | null;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  type: string; // Tipo dinamico dal database
  title: string;
  description: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateActivityRequest {
  practiceId: string;
  userId?: string;
  type: Activity['type'];
  title: string;
  description?: string;
  dueDate?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
}

export interface UpdateActivityRequest {
  userId?: string;
  type?: Activity['type'];
  title?: string;
  description?: string;
  status?: Activity['status'];
  dueDate?: string;
  completedAt?: string;
}

export interface ActivityQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  practiceId?: string;
  userId?: string;
  type?: Activity['type'];
  status?: Activity['status'] | 'OPEN' | 'ACTIVE';
  dueDateStart?: string;
  dueDateEnd?: string;
  groupByPractice?: boolean;
}

export interface ActivitiesResponse {
  success: boolean;
  data: Activity[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    totalActivities?: number;
  };
}

export const activityService = {
  async getActivities(params?: ActivityQueryParams): Promise<ActivitiesResponse> {
    const response = await apiClient.get('/activities', { params });
    return response.data;
  },

  async getActivity(id: string): Promise<{ success: boolean; data: Activity }> {
    const response = await apiClient.get(`/activities/${id}`);
    return response.data;
  },

  async createActivity(data: CreateActivityRequest): Promise<{ success: boolean; data: Activity; message: string }> {
    const response = await apiClient.post('/activities', data);
    return response.data;
  },

  async updateActivity(id: string, data: UpdateActivityRequest): Promise<{ success: boolean; data: Activity; message: string }> {
    const response = await apiClient.put(`/activities/${id}`, data);
    return response.data;
  },

  async deleteActivity(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/activities/${id}`);
    return response.data;
  },

  async updateActivityStatus(id: string, status: Activity['status']): Promise<{ success: boolean; data: Activity; message: string }> {
    const response = await apiClient.put(`/activities/${id}/status`, { status });
    return response.data;
  },

  async completeActivity(id: string): Promise<{ success: boolean; data: Activity; message: string }> {
    const response = await apiClient.post(`/activities/${id}/complete`);
    return response.data;
  },

  async getActivitiesByPractice(practiceId: string): Promise<{ success: boolean; data: Activity[]; count: number }> {
    const response = await apiClient.get(`/activities/practice/${practiceId}`);
    return response.data;
  },

  async getActivitiesByUser(userId: string, params?: ActivityQueryParams): Promise<{ success: boolean; data: Activity[]; count: number }> {
    const response = await apiClient.get(`/activities/user/${userId}`, { params });
    return response.data;
  },

  async bulkAssign(activityIds: string[], userId: string): Promise<{ success: boolean; message: string; data: { count: number } }> {
    const response = await apiClient.post('/activities/bulk-assign', { activityIds, userId });
    return response.data;
  },
};

